import React from 'react';
import styled from 'styled-components';
import { FaTimes, FaCheckCircle, FaExclamationCircle, FaInfoCircle, FaQuestionCircle } from 'react-icons/fa';

const Overlay = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'isVisible'
})<{ isVisible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: ${props => props.isVisible ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 10000;
  animation: fadeIn 0.2s ease;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const AlertBox = styled.div`
  background: ${props => props.theme.surface};
  border-radius: 12px;
  padding: 2rem;
  max-width: 90%;
  width: 400px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  animation: slideUp 0.3s ease;
  position: relative;

  @keyframes slideUp {
    from { 
      transform: translateY(20px);
      opacity: 0;
    }
    to { 
      transform: translateY(0);
      opacity: 1;
    }
  }

  @media (max-width: 480px) {
    width: 90%;
    padding: 1.5rem;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: transparent;
  border: none;
  color: ${props => props.theme.textSecondary};
  cursor: pointer;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &:hover {
    color: ${props => props.theme.textPrimary};
    transform: scale(1.1);
  }
`;

const IconWrapper = styled.div<{ type: AlertType }>`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 auto 1.5rem;
  font-size: 2.5rem;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  border: 3px solid;
  
  color: ${props => {
    switch (props.type) {
      case 'success': return props.theme.success || '#28a745';
      case 'error': return props.theme.error || '#dc3545';
      case 'warning': return props.theme.warning || '#ffc107';
      case 'info': return props.theme.info || '#17a2b8';
      case 'confirm': return props.theme.primary || '#F40000';
      default: return props.theme.textPrimary;
    }
  }};
  
  border-color: ${props => {
    switch (props.type) {
      case 'success': return props.theme.success || '#28a745';
      case 'error': return props.theme.error || '#dc3545';
      case 'warning': return props.theme.warning || '#ffc107';
      case 'info': return props.theme.info || '#17a2b8';
      case 'confirm': return props.theme.primary || '#F40000';
      default: return props.theme.textPrimary;
    }
  }};
`;

const Title = styled.h3`
  color: ${props => props.theme.textPrimary};
  margin-bottom: 0.5rem;
  text-align: center;
  font-size: 1.2rem;
`;

const Message = styled.p`
  color: ${props => props.theme.textSecondary};
  text-align: center;
  line-height: 1.6;
  margin-bottom: 1.5rem;
  white-space: pre-line;
  font-size: 1rem;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  min-width: 100px;

  ${props => props.variant === 'secondary' ? `
    background: ${props.theme.buttonSecondary || '#6c757d'};
    color: ${props.theme.white};
    
    &:hover {
      background: ${props.theme.buttonSecondaryHover || '#5a6268'};
      transform: translateY(-2px);
    }
  ` : `
    background: ${props.theme.primary || '#F40000'};
    color: ${props.theme.white};
    
    &:hover {
      background: ${props.theme.primaryDark || '#B50000'};
      transform: translateY(-2px);
    }
  `}

  &:active {
    transform: translateY(0);
  }
`;

type AlertType = 'success' | 'error' | 'warning' | 'info' | 'confirm';

interface CustomAlertProps {
  isVisible: boolean;
  type?: AlertType;
  title?: string;
  message: string;
  onClose?: () => void;
  onConfirm?: () => void;
  confirmText?: string;
  cancelText?: string;
  showCloseButton?: boolean;
}

const CustomAlert: React.FC<CustomAlertProps> = ({
  isVisible,
  type = 'info',
  title,
  message,
  onClose,
  onConfirm,
  confirmText = 'OK',
  cancelText = 'Cancel',
  showCloseButton = true
}) => {
  const getIcon = () => {
    switch (type) {
      case 'success':
        return React.createElement(FaCheckCircle as any, {});
      case 'error':
        return React.createElement(FaExclamationCircle as any, {});
      case 'warning':
        return React.createElement(FaExclamationCircle as any, {});
      case 'confirm':
        return React.createElement(FaQuestionCircle as any, {});
      default:
        return React.createElement(FaInfoCircle as any, {});
    }
  };

  const getTitle = () => {
    if (title) return title;
    switch (type) {
      case 'success': return 'Success';
      case 'error': return 'Error';
      case 'warning': return 'Warning';
      case 'confirm': return 'Confirm';
      default: return 'Information';
    }
  };

  return (
    <Overlay isVisible={isVisible} onClick={onClose}>
      <AlertBox onClick={(e) => e.stopPropagation()}>
        {showCloseButton && onClose && (
          <CloseButton onClick={onClose}>
            {React.createElement(FaTimes as any, { size: 20 })}
          </CloseButton>
        )}
        
        <IconWrapper type={type}>
          {getIcon()}
        </IconWrapper>
        
        <Title>{getTitle()}</Title>
        <Message>{message}</Message>
        
        <ButtonGroup>
          {type === 'confirm' ? (
            <>
              <Button variant="secondary" onClick={onClose}>
                {cancelText}
              </Button>
              <Button variant="primary" onClick={onConfirm}>
                {confirmText}
              </Button>
            </>
          ) : (
            <Button variant="primary" onClick={onClose || onConfirm}>
              {confirmText}
            </Button>
          )}
        </ButtonGroup>
      </AlertBox>
    </Overlay>
  );
};

export default CustomAlert;